import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

let quickAddRequestId = 0;

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    const sourceUrl = productLink?.href || this.dataset.productUrl;
    if (!sourceUrl) return '';

    const url = new URL(sourceUrl, window.location.origin);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  get productNavigationUrl() {
    const sourceUrl = this.productPageUrl;
    if (!sourceUrl) return '';
    const url = new URL(sourceUrl, window.location.origin);
    if (this.closest('product-card')?.dataset.sizeCollection !== 'true') url.searchParams.delete('variant');
    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || this.dataset.variantId || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const requestId = ++quickAddRequestId;
    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const expectedProductId = this.dataset.productId;
        const gridElement = Array.from(html.querySelectorAll('[data-product-grid-content]')).find(
          (grid) => !expectedProductId || grid.querySelector(`[data-product-id="${expectedProductId}"]`)
        );
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (!productGrid || requestId !== quickAddRequestId) return;

    // Use a fresh clone from the cache
    const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
    await this.updateQuickAddModal(freshContent);

    if (requestId !== quickAddRequestId) return;
    this.#openQuickAddModal();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    const sizeCollection = this.closest('product-card')?.dataset.sizeCollection === 'true';
    const quickAddDialog = modalContent.closest('quick-add-dialog');
    if (quickAddDialog) quickAddDialog.dataset.sizeCollection = String(sizeCollection);

    const isCtlQuickView = this.dataset.ctlQuickView === 'true';

    const ctlQuickViewContent = isCtlQuickView ? this.#prepareCtlQuickView(productGrid) : null;

    if (!isCtlQuickView && isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productNavigationUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    if (isCtlQuickView) {
      modalContent.className = `${productGrid.className} quick-add-modal__content`;
      modalContent.dataset.ctlQuickView = 'true';
      modalContent.style.removeProperty('column-gap');
      if (!ctlQuickViewContent) return;
      modalContent.replaceChildren(ctlQuickViewContent.body, ctlQuickViewContent.footer);
      const header = document.querySelector('.header-section');
      const headerBottom = header?.getBoundingClientRect().bottom || 0;
      const dialog = modalContent.closest('dialog');
      if (dialog) {
        dialog.dataset.ctlQuickView = 'true';
        dialog.style.setProperty('--ctl-quick-view-top', `${Math.max(headerBottom, 0) + 30}px`);
      }
    } else {
      delete modalContent.dataset.ctlQuickView;
      modalContent.style.removeProperty('column-gap');
      const dialog = modalContent.closest('dialog');
      if (dialog) {
        delete dialog.dataset.ctlQuickView;
        dialog.style.removeProperty('--ctl-quick-view-top');
      }
      morph(modalContent, productGrid);
    }

    const dialog = modalContent.closest('dialog');
    const title = isCtlQuickView ? modalContent.querySelector('.view-product-title') : null;
    if (dialog && title) {
      title.id = 'quick-add-modal-content-title';
      dialog.setAttribute('aria-labelledby', title.id);
    } else if (dialog?.getAttribute('aria-labelledby') === 'quick-add-modal-content-title') {
      dialog.removeAttribute('aria-labelledby');
    }

    if (!isCtlQuickView) this.#syncVariantSelection(modalContent);
  }

  /** @param {Element} productGrid */
  #prepareCtlQuickView(productGrid) {
    const media = productGrid.querySelector('.product-information__media');
    const title = productGrid.querySelector('.view-product-title');
    const variantPicker = productGrid.querySelector('variant-picker');
    const descriptions = Array.from(productGrid.querySelectorAll('.product-details .text-block.rte'));
    const description = descriptions.sort(
      (first, second) => (second.textContent?.trim().length || 0) - (first.textContent?.trim().length || 0)
    )[0];

    const details = document.createElement('div');
    details.className = 'ctl-quick-view__details';

    if (title) details.appendChild(title);

    const price = document.createElement('div');
    price.className = 'ctl-quick-view__price';
    price.dataset.ctlQuickViewPrice = '';
    price.setAttribute('aria-live', 'polite');
    price.textContent = this.dataset.initialPrice || '';
    details.appendChild(price);

    if (variantPicker) details.appendChild(variantPicker);
    if (description) {
      description.classList.add('ctl-quick-view__description');
      details.appendChild(description);
    }

    const footer = document.createElement('div');
    footer.className = 'ctl-quick-view__footer';
    const link = document.createElement('a');
    link.className = 'ctl-quick-view__details-link';
    link.href = this.productNavigationUrl;
    link.textContent = this.dataset.detailsLabel || '';
    link.insertAdjacentHTML('beforeend', '<span aria-hidden="true">→</span>');
    footer.appendChild(link);

    const body = document.createElement('div');
    body.className = 'ctl-quick-view__body';
    body.append(...[media, details].filter(Boolean));

    return { body, footer };
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener('change', this.#updateCtlQuickViewPrice, { signal: this.#abortController.signal });

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (
      this.querySelector('.ctl-quick-view__details-link')
    );
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    const navigationUrl = new URL(anchorElement.href);
    if (this.dataset.sizeCollection === 'true' && event.detail.resource?.id) {
      navigationUrl.searchParams.set('variant', event.detail.resource.id);
    } else {
      navigationUrl.searchParams.delete('variant');
    }
    if (viewMoreDetailsLink) viewMoreDetailsLink.href = navigationUrl.toString();
    if (mobileProductTitle) mobileProductTitle.href = navigationUrl.toString();
  };

  #updateCtlQuickViewPrice = (event) => {
    if (!(event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement)) return;
    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;
    const price = this.querySelector('[data-ctl-quick-view-price]');
    if (price && selectedOption.dataset.variantPrice) price.textContent = selectedOption.dataset.variantPrice;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
